import React, { useState } from 'react';
import { X, Palette } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';

const EditFieldModal = ({ field, onSave, onClose }) => {
  const [label, setLabel] = useState(field.label);
  const [placeholder, setPlaceholder] = useState(field.placeholder || '');
  const [required, setRequired] = useState(field.required);
  const [maxLength, setMaxLength] = useState(field.maxLength || '');
  const [dateRestriction, setDateRestriction] = useState(
    field.dateRestriction || 'none'
  );
  const [minDate, setMinDate] = useState(field.minDate || '');
  const [maxDate, setMaxDate] = useState(field.maxDate || '');
  const [options, setOptions] = useState(
    field.options ? field.options.join(', ') : ''
  );
  const [styles, setStyles] = useState({
    labelColor: field.labelColor || '#000000',
    backgroundColor: field.backgroundColor || '#ffffff',
    borderColor: field.borderColor || '#e5e7eb',
    borderRadius: field.borderRadius || 'rounded',
    padding: field.padding || 'p-4',
  });
  const [validationType, setValidationType] = useState(
    field.validationType || 'none'
  );
  const [newOption, setNewOption] = useState('');
  const [isEditingOption, setIsEditingOption] = useState(false);
  const [buttonSettings, setButtonSettings] = useState({
    text: 'Enviar',
    backgroundColor: '#2563eb',
    textColor: '#ffffff',
    borderRadius: 'rounded',
    padding: 'p-4',
    width: 'w-full',
  });
  // Estado para la opción "Otro"
  const [includeOtherOption, setIncludeOtherOption] = useState(
    field.includeOtherOption !== undefined ? field.includeOtherOption : true
  );
  const [otherOptionPlaceholder, setOtherOptionPlaceholder] = useState(
    field.otherOptionPlaceholder || 'Especifique otra opción...'
  );

  const handleAddOption = () => {
    if (newOption.trim()) {
      setOptions(prevOptions => {
        const currentOptions = prevOptions
          ? prevOptions.split(',').map(opt => opt.trim())
          : [];
        return [...currentOptions, newOption].join(', ');
      });
      setNewOption('');
      setIsEditingOption(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const handleSave = () => {
    const cleanedOptions = options
      .split(',')
      .map(opt => opt.trim())
      .filter(opt => opt); // Remove empty options

    // Process date restrictions
    let dateSettings = {};
    if (field.type === 'date') {
      dateSettings = {
        dateRestriction,
        minDate:
          dateRestriction === 'range' && minDate
            ? new Date(minDate).toISOString()
            : null,
        maxDate:
          dateRestriction === 'range' && maxDate
            ? new Date(maxDate).toISOString()
            : null,
      };

      // Validate date range if restriction type is 'range'
      if (dateRestriction === 'range') {
        if (new Date(minDate) > new Date(maxDate)) {
          alert('La fecha mínima no puede ser mayor que la fecha máxima');
          return;
        }
      }
    }

    setButtonSettings(prev => ({
      ...prev,
      text: prev.text || 'Enviar',
      backgroundColor: prev.backgroundColor || '#2563eb',
      textColor: prev.textColor || '#ffffff',
      borderRadius: prev.borderRadius || 'rounded',
      padding: prev.padding || 'p-4',
      width: prev.width || 'w-full',
    }));

    onSave({
      ...field,
      label,
      placeholder,
      required,
      options: cleanedOptions,
      validationType,
      maxLength: maxLength || '', // Add this line
      ...dateSettings,
      validationPattern: validationPatterns[validationType], // Add this line
      ...styles,
      dateRestriction,
      minDate: dateRestriction === 'range' ? minDate : null,
      maxDate: dateRestriction === 'range' ? maxDate : null,
      submitButton: buttonSettings,
      // Añadimos la configuración para la opción "Otro"
      includeOtherOption,
      otherOptionPlaceholder,
    });
  };

  // Add dateRestrictionOptions after other option arrays
  const dateRestrictionOptions = [
    { label: 'Sin restricción', value: 'none' },
    { label: 'Solo fechas pasadas', value: 'past' },
    { label: 'Solo fechas futuras', value: 'future' },
    { label: 'Rango de fechas', value: 'range' },
  ];

  const handleInputValidation = (e, validationType) => {
    if (!validationType || validationType === 'none') return;

    const value = e.target.value;
    const pattern = validationPatterns[validationType];

    if (!pattern.test(value)) {
      e.preventDefault();
      e.target.value = value.slice(0, -1);
    }
  };

  const borderRadiusOptions = [
    { label: 'Ninguno', value: '' },
    { label: 'Pequeño', value: 'rounded' },
    { label: 'Mediano', value: 'rounded-md' },
    { label: 'Grande', value: 'rounded-lg' },
  ];

  const paddingOptions = [
    { label: 'Pequeño', value: 'p-2' },
    { label: 'Mediano', value: 'p-4' },
    { label: 'Grande', value: 'p-6' },
  ];

  const validationOptions = [
    { label: 'Ninguno', value: 'none' },
    { label: 'Solo letras', value: 'letters' },
    { label: 'Solo números', value: 'numbers' },
    { label: 'Alfanumérico', value: 'alphanumeric' },
    { label: 'Email', value: 'email' },
  ];

  // Add validation patterns
  const validationPatterns = {
    letters: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/,
    numbers: /^[0-9]*$/,
    alphanumeric: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]*$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^[0-9-+\s()]*$/,
  };

  // Añade esta configuración para ReactQuill
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link'], // Añade la opción de enlaces
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

  const sanitizeConfig = {
    ADD_ATTR: ['target', 'rel'],
    ADD_TAGS: ['a'],
    ALLOWED_TAGS: ['p', 'a', 'strong', 'em', 'span', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    WHOLE_DOCUMENT: false,
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: {
          ...attribs,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: 'color: #2563eb; text-decoration: underline; cursor: pointer;',
        },
      }),
    },
  };

  // Modifica el useEffect existente
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
    .ql-editor a {
      color: #2563eb !important;
      text-decoration: underline !important;
      cursor: pointer !important;
    }
    .ql-editor a:hover {
      color: #1d4ed8 !important;
    }
    .preview-content a {
      color: #2563eb !important;
      text-decoration: underline !important;
      cursor: pointer !important;
    }
    .preview-content a:hover {
      color: #1d4ed8 !important;
    }
    /* Estilos adicionales para el editor de opciones */
    .ql-toolbar.ql-snow {
      border-top-left-radius: 0.375rem;
      border-top-right-radius: 0.375rem;
    }
    .ql-container.ql-snow {
      border-bottom-left-radius: 0.375rem;
      border-bottom-right-radius: 0.375rem;
    }
  `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-base-100 z-10 flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-base-content">
            Editar Campo
          </h2>
          <button
            onClick={onClose}
            className="p-1 bg-base-100 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5 text-base-content" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Campo básico */}
          <div className="space-y-2">
            <label className="block text-sm font-medium">Pregunta:</label>
            <ReactQuill
              value={label}
              onChange={setLabel}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          {(field.type === 'input' || field.type === 'textarea') && (
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Marcador campo:
              </label>
              <input
                type="text"
                value={placeholder}
                onChange={e => setPlaceholder(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          )}

          {(field.type === 'input' || field.type === 'textarea') && (
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Tipo de Validación:
              </label>
              <select
                value={validationType}
                onChange={e => setValidationType(e.target.value)}
                className="w-full p-2 border rounded"
              >
                {validationOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(field.type === 'input' || field.type === 'textarea') && (
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Límite de caracteres:
              </label>
              <input
                type="number"
                value={maxLength}
                onChange={e => setMaxLength(e.target.value)}
                min="1"
                max="1000"
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Dejar vacío para sin límite"
              />
            </div>
          )}

          {/* Add this inside the <div className="p-6 space-y-4"> section */}
          {field.type === 'date' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">
                  Restricción de fecha:
                </label>
                <select
                  value={dateRestriction}
                  onChange={e => setDateRestriction(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  {dateRestrictionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {dateRestriction === 'range' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">
                      Fecha mínima:
                    </label>
                    <input
                      type="date"
                      value={minDate ? minDate.split('T')[0] : ''}
                      onChange={e => setMinDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      required={dateRestriction === 'range'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Fecha máxima:
                    </label>
                    <input
                      type="date"
                      value={maxDate ? maxDate.split('T')[0] : ''}
                      onChange={e => setMaxDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      required={dateRestriction === 'range'}
                    />
                  </div>
                </div>
              )}

              {/* Preview for date restrictions */}
              <div className="mt-2 text-sm text-gray-600">
                {dateRestriction === 'past' && (
                  <p>
                    Los usuarios solo podrán seleccionar fechas anteriores a hoy
                  </p>
                )}
                {dateRestriction === 'future' && (
                  <p>
                    Los usuarios solo podrán seleccionar fechas posteriores a
                    hoy
                  </p>
                )}
                {dateRestriction === 'range' && (
                  <p>
                    Los usuarios solo podrán seleccionar fechas entre {minDate}{' '}
                    y {maxDate}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Estilos */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Palette size={18} />
              <span className="font-medium">Estilos del campo</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Color del texto
                </label>
                <input
                  type="color"
                  value={styles.labelColor}
                  onChange={e =>
                    setStyles({ ...styles, labelColor: e.target.value })
                  }
                  className="w-full h-10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Color de fondo
                </label>
                <input
                  type="color"
                  value={styles.backgroundColor}
                  onChange={e =>
                    setStyles({ ...styles, backgroundColor: e.target.value })
                  }
                  className="w-full h-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Color del borde
              </label>
              <input
                type="color"
                value={styles.borderColor}
                onChange={e =>
                  setStyles({ ...styles, borderColor: e.target.value })
                }
                className="w-full h-10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Bordes redondeados
              </label>
              <select
                value={styles.borderRadius}
                onChange={e =>
                  setStyles({ ...styles, borderRadius: e.target.value })
                }
                className="w-full p-2 border rounded"
              >
                {borderRadiusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Espacio borde
              </label>
              <select
                value={styles.padding}
                onChange={e =>
                  setStyles({ ...styles, padding: e.target.value })
                }
                className="w-full p-2 border rounded"
              >
                {paddingOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Opciones adicionales */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={required}
              onChange={e => setRequired(e.target.checked)}
              className="w-4 h-4"
            />
            <label className="text-sm font-medium">Campo requerido</label>
          </div>

          {/* Opciones para select/radio/checkbox */}
          {['select', 'radio', 'checkbox'].includes(field.type) && (
            <div className="space-y-4">
              {/* Opción "Otro" */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeOtherOption}
                    onChange={e => setIncludeOtherOption(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium">
                    Incluir opción "Otro"
                  </label>
                </div>

                {includeOtherOption && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">
                      Marcador para campo "Otro":
                    </label>
                    <input
                      type="text"
                      value={otherOptionPlaceholder}
                      onChange={e => setOtherOptionPlaceholder(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Especifique otra opción..."
                    />
                  </div>
                )}

                {includeOtherOption && (
                  <div className="mt-2 text-sm text-gray-600">
                    <p>
                      Los usuarios podrán seleccionar "Otro" y especificar una
                      opción personalizada
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium">Opciones:</label>
                <div className="flex flex-col space-y-2">
                  <div className="flex space-x-2">
                    {isEditingOption ? (
                      <div className="w-full">
                        <ReactQuill
                          value={newOption}
                          onChange={setNewOption}
                          modules={modules}
                          formats={formats}
                          className="w-full border rounded-md"
                        />
                        <div className="flex justify-end mt-2 space-x-2 bg-base-100">
                          <button
                            type="button"
                            onClick={() => setIsEditingOption(false)}
                            className="px-3 py-2 text-base-content border rounded-md hover:bg-gray-50"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={handleAddOption}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Añadir
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditingOption(true)}
                        className="w-full px-3 py-2 text-left border rounded-md hover:bg-gray-50"
                      >
                        + Añadir opción
                      </button>
                    )}
                  </div>
                  <div className="mt-2 space-y-2">
                    {options.split(',').map((option, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 border rounded-md"
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              option.trim(),
                              sanitizeConfig
                            ),
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newOptions = options
                              .split(',')
                              .filter((_, i) => i !== index)
                              .join(', ');
                            setOptions(newOptions);
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Vista previa */}
          <div
            className="font-medium"
            dangerouslySetInnerHTML={{
              __html:
                DOMPurify.sanitize(label, sanitizeConfig) || 'Vista previa',
            }}
          />
          {maxLength && (
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-md"
              placeholder={placeholder}
              maxLength={maxLength}
              disabled
            />
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-base-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFieldModal;
